import { VStack, PopoverAnchor, HStack, Box } from '@chakra-ui/react';
import {
  ChevronsRightIcon,
  ArrowBarToLeftIcon,
  ArrowBarToRightIcon,
  ArrowLeftIcon,
} from 'Tokens/Icons/Direction';
import { SideBarPopover } from '../../../DisclosureRequirementsUtilsComponents';
import { PopoverTypes, SidebarModes } from '../../../Requirement';
import { Button, IconButton } from 'Atoms';
import { useEffect, useMemo, useRef } from 'react';
import { useUserSetting } from 'containers/Navigation';
import { Typography } from 'Tokens';
import { GetDisclosureRequirementQuery_ } from 'models';
import { SidebarMenu } from './SidebarMenu';
import { DRViews, getAvailableTabs } from '../DisclosureRequirement.utils';
import { SubsidiaryWarning } from 'containers/Esrs/pieces/SubsidiaryWarning';
import { useNavigate, useParams } from 'react-router-dom';
import { TOP_MENU_HEIGHT } from 'Molecules';

export const DisclosureRequirementSidebar = ({
  disclosureRequirement,
  sidebarMode,
  setSidebarMode,
  showReportPreview,
  selectedView,
  setSelectedView,
  hasAccessToAllSubs,
}: {
  disclosureRequirement: GetDisclosureRequirementQuery_['DisclosureRequirement'];
  sidebarMode: SidebarModes;
  setSidebarMode: (val: SidebarModes) => void;
  showReportPreview: boolean;
  selectedView: DRViews;
  setSelectedView: (val: DRViews) => void;
  hasAccessToAllSubs?: boolean;
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { companyId = '', esrsAssessmentId = '', framework = '', standardRef = '' } = useParams();
  const [isOpenedTooltipDismissed, setOpenedTooltipDismissed] = useUserSetting(
    'sidebar-opened-tootlip-dismissed',
    false
  );
  const [isClosedTooltipDismissed, setClosedTooltipDismissed] = useUserSetting(
    'sidebar-closed-tootlip-dismissed',
    false
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpenedTooltipDismissed && !isClosedTooltipDismissed) setSidebarMode(SidebarModes.open);
  }, [isOpenedTooltipDismissed, isClosedTooltipDismissed]);

  const [isSidebarClosed, isSidebarOverlay, isSidebarOpen] = useMemo(
    () => [
      sidebarMode === SidebarModes.closed,
      sidebarMode === SidebarModes.overlay,
      sidebarMode === SidebarModes.open,
    ],
    [sidebarMode]
  );

  const availableTabs = useMemo(
    () => getAvailableTabs(showReportPreview, selectedView, setSelectedView),
    [showReportPreview, selectedView, setSelectedView]
  );

  const handleSidebar = () => {
    if (isSidebarClosed) setSidebarMode(SidebarModes.overlay);
    else if (isSidebarOverlay) setSidebarMode(SidebarModes.open);
    else setSidebarMode(SidebarModes.closed);
  };

  return (
    <SideBarPopover
      popoverType={PopoverTypes.closed}
      isSidebarClosed={isSidebarClosed}
      isOpenedTooltipDismissed={isOpenedTooltipDismissed}
      isClosedTooltipDismissed={isClosedTooltipDismissed}
      setClosedTooltipDismissed={setClosedTooltipDismissed}
      setOpenedTooltipDismissed={setOpenedTooltipDismissed}
    >
      <VStack
        height={`calc(100vh - ${TOP_MENU_HEIGHT})`}
        ref={sidebarRef}
        w={isSidebarClosed ? '36px' : '288px'}
        maxW="288px"
        minW={isSidebarOpen ? '288px' : '0px'}
        borderRight="1px solid"
        borderRightColor="border.decorative"
        overflow="hidden"
        bg="bg.default"
        alignItems="start"
        justifyContent={isSidebarClosed ? 'center' : 'unset'}
        position={isSidebarOpen ? 'relative' : 'absolute'}
        gap="0px"
        onMouseEnter={() => {
          const sidebarWidth = sidebarRef?.current?.offsetWidth;
          if (sidebarWidth === 36 && isSidebarClosed) {
            setSidebarMode(SidebarModes.overlay);
            if (!isClosedTooltipDismissed) {
              setClosedTooltipDismissed(true);
            }
          }
        }}
        onMouseLeave={() => (isSidebarOverlay ? setSidebarMode(SidebarModes.closed) : {})}
        zIndex="5"
        transition="all 0.15s ease-in-out"
      >
        {isSidebarClosed && (
          <PopoverAnchor>
            <IconButton
              opacity={isSidebarClosed ? 1 : 0}
              marginInline="auto"
              variant="ghost"
              icon={<ChevronsRightIcon color="text.default" />}
              aria-label="open"
              transition="all 0.15s"
              position="absolute"
              left="0px"
            />
          </PopoverAnchor>
        )}
        <VStack
          opacity={isSidebarClosed ? 0 : 1}
          pointerEvents={isSidebarClosed ? 'none' : 'auto'}
          transition="all 0.15s ease-in-out"
          alignItems="start"
          gap="0px"
          height="100%"
          w="100%"
          position={isSidebarClosed ? 'absolute' : 'unset'}
        >
          <HStack justifyContent="space-between" w="100%">
            <Button
              size="sm"
              variant="ghost"
              aria-label={'Back'}
              leftIcon={<ArrowLeftIcon />}
              mt="8px"
              ml="8px"
              onClick={() =>
                navigate(
                  `/${companyId}/sustainability-reporting/${framework}/${esrsAssessmentId}/standard/${standardRef}/`
                )
              }
            >
              Back
            </Button>

            <SideBarPopover
              popoverType={PopoverTypes.open}
              isSidebarClosed={isSidebarClosed}
              isOpenedTooltipDismissed={isOpenedTooltipDismissed}
              isClosedTooltipDismissed={isClosedTooltipDismissed}
              setClosedTooltipDismissed={setClosedTooltipDismissed}
              setOpenedTooltipDismissed={setOpenedTooltipDismissed}
            >
              <PopoverAnchor>
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={isSidebarOpen ? <ArrowBarToLeftIcon /> : <ArrowBarToRightIcon />}
                  aria-label="hide"
                  mt="8px"
                  mr="8px"
                  onClick={(e) => {
                    handleSidebar();
                    if (!isOpenedTooltipDismissed) {
                      setOpenedTooltipDismissed(true);
                    }
                    e.stopPropagation();
                  }}
                />
              </PopoverAnchor>
            </SideBarPopover>
          </HStack>
          <VStack spacing="16px" alignItems="start" w="100%">
            <VStack spacing="16px" alignItems="stretch" w="100%">
              <VStack p="24px 16px" pb="16px" spacing="4px" alignItems="start">
                <Typography variant="h2">{disclosureRequirement?.title}</Typography>
                <Typography variant="body" color="text.muted">
                  {disclosureRequirement?.reference}
                </Typography>
              </VStack>
              {!hasAccessToAllSubs && (
                <Box paddingInline="16px">
                  <SubsidiaryWarning />
                </Box>
              )}
              <SidebarMenu options={availableTabs} />
            </VStack>
          </VStack>
        </VStack>
      </VStack>
    </SideBarPopover>
  );
};
